/**
 * PHOTO BYTES, STORED SEPARATELY FROM THE OUTBOX ROW.
 * ==================================================
 *
 * `outbox.ts` holds small JSON payloads and rewrites a row on every state transition (queued →
 * in-flight → queued-with-backoff → …). A 4 MB photo on that row would be read, structured-cloned
 * and rewritten each time, so photos live here and the outbox payload carries a `blobId` string.
 *
 * **STORED AS `ArrayBuffer` + MIME TYPE, NOT AS A `Blob`.** Storing `Blob`s directly in IndexedDB is
 * in the spec and works in current browsers, but WebKit has a history of Blob-in-IDB bugs where the
 * backing file is collected while the record survives — which yields a record that reads back as a
 * zero-length or unreadable Blob. That failure is silent and it destroys exactly the artefact this
 * app exists to capture, so we store the bytes we control and rebuild the Blob on read. The cost is
 * one copy on write; the benefit is that a photo either round-trips or the read throws.
 *
 * NO REFERENCE COUNTING, AND THAT IS A DECISION. `deleteBlob` is explicit and is called by the sync
 * engine only after the parent outbox item reaches `synced`. An orphaned blob wastes quota; a blob
 * deleted while its item is still queued loses a photo permanently. Given only one of those is
 * recoverable, orphans are the correct direction to fail, and `listBlobs()` exists so a housekeeping
 * pass (and the storage panel) can see them rather than pretending they cannot happen.
 */

import { BLOB_STORE, request, withTransaction } from './db';
import { newBlobId } from './ids';

interface StoredBlob {
  readonly blobId: string;
  readonly type: string;
  readonly bytes: ArrayBuffer;
  readonly storedAt: number;
}

/**
 * Persist a blob and return its id. Resolves only once the transaction has COMMITTED, so a caller
 * that has the id knows the bytes are on disk.
 */
export async function putBlob(blob: Blob): Promise<string> {
  const blobId = newBlobId();
  const bytes = await blob.arrayBuffer();
  const record: StoredBlob = {
    blobId,
    // A Blob constructed with no type reads back as `''`; preserved as-is rather than guessed,
    // because a wrong `Content-Type` on the presigned PUT fails the S3 signature (the mint route
    // signs the content type into the URL) and a guess would make that failure mysterious.
    type: blob.type,
    bytes,
    storedAt: Date.now(),
  };
  await withTransaction(BLOB_STORE, 'readwrite', (tx) => {
    tx.objectStore(BLOB_STORE).put(record);
  });
  return blobId;
}

/** The blob, or `undefined` when there is no such id. */
export async function getBlob(blobId: string): Promise<Blob | undefined> {
  const record = await withTransaction(BLOB_STORE, 'readonly', (tx) =>
    request<StoredBlob | undefined>(
      tx.objectStore(BLOB_STORE).get(blobId) as IDBRequest<StoredBlob | undefined>,
    ),
  );
  if (record === undefined) {
    return undefined;
  }
  return new Blob([record.bytes], { type: record.type });
}

/** Removes a blob. Called only after its parent outbox item is `synced`. */
export async function deleteBlob(blobId: string): Promise<void> {
  await withTransaction(BLOB_STORE, 'readwrite', (tx) => {
    tx.objectStore(BLOB_STORE).delete(blobId);
  });
}

/** Every stored blob id, with its size. For the storage panel and for orphan housekeeping. */
export async function listBlobs(): Promise<readonly { blobId: string; bytes: number; storedAt: number }[]> {
  const records = await withTransaction(BLOB_STORE, 'readonly', (tx) =>
    request<StoredBlob[]>(tx.objectStore(BLOB_STORE).getAll() as IDBRequest<StoredBlob[]>),
  );
  return records.map((record) => ({
    blobId: record.blobId,
    bytes: record.bytes.byteLength,
    storedAt: record.storedAt,
  }));
}
